import { Component, Inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { collection, doc, Firestore, getDoc, getDocs, orderBy, query } from '@angular/fire/firestore';
import { ConceptRow, ObsPoint, ReplayFrame, Series } from '../../interfaces/master-interfaces';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AnonymousUserService } from '../../services/user services/anonymous-user.service';

/**
 * BEING HISTORY
 * -------------
 * Replays a being's whole observation stream from birth and reconstructs the
 * curves the backend never persists.
 *
 * `metadata/knowledgeState` holds only the *latest* maturity and
 * predictionAccuracy — there is no time series. But both are pure functions of
 * data stored on every rawObservation, so we recompute them here using the same
 * formulas as `updateKnowledgeState`. Keep the two in sync: if the backend
 * formula changes, change REPLAY below.
 *
 * Observations are ordered by `timestamp`, not `tick`. `lastSimulatedTick` is
 * never incremented in the tick function, so every observation carries the same
 * tick value and ordering by it is meaningless.
 */



const NOVEL_WINDOW = 20;
const CHART_W = 720;
const CHART_H = 190;
const PAD_L = 8;
const PAD_R = 8;
const PAD_T = 12;
const PAD_B = 22;



@Component({
  selector: 'app-being-history',
  standalone: true,
  imports: [
    CommonModule
  ],
  templateUrl: './being-history.component.html',
  styleUrl: './being-history.component.scss'
})


export class BeingHistoryComponent implements OnChanges {



  readonly CHART_W = CHART_W;
  readonly CHART_H = CHART_H;
  readonly PAD_L = PAD_L;
  readonly PAD_R = PAD_R;
  readonly PAD_T = PAD_T;
  readonly PAD_B = PAD_B;

  userId!: string;
  loading = true;
  beingName = '';
  alive = true;
  deathTick: number | null = null;
  genomeRows: Array<{ k: string; v: number }> = [];
  beingId!: string;
  frames: ReplayFrame[] = [];
  series: Series[] = [];
  conceptRows: ConceptRow[] = [];
  findings: Array<{ title: string; body: string; flag?: boolean }> = [];
  saturationX: number | null = null;
  chartAria = '';

  private obs: ObsPoint[] = [];

  constructor(
    private firestore: Firestore,
    private _anonymousUserService: AnonymousUserService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) { }

  ngOnInit(): void {
    this.beingId = this.data?.beingId ?? '';

    if (!this.userId) {
      this.userId = this._anonymousUserService.currentUserId ?? '';
    }

    if (this.beingId) {
      void this.load();
    } else {
      this.loading = false;   // fall through to the empty state, not a spinner
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['beingId'] && this.beingId) {
      void this.load();
    }
  }

  spanOf(c: ConceptRow): string {
    if (!c.firstEncountered || !c.lastEncountered) return '—';
    const hours = (c.lastEncountered - c.firstEncountered) / 3_600_000;
    if (hours < 1) return 'one visit';
    if (hours < 48) return `${Math.round(hours)} h`;
    return `${Math.round(hours / 24)} d`;
  }

  // ================================================================
  // LOAD
  // ================================================================

  private async load(): Promise<void> {
    this.loading = true;
    this.frames = [];
    this.series = [];
    this.conceptRows = [];
    this.findings = [];
    this.saturationX = null;

    const beingRef = doc(this.firestore, 'beings', this.beingId);

    const [beingSnap, obsSnap, conceptSnap, ksSnap] = await Promise.all([
      getDoc(beingRef),
      getDocs(
        query(
          collection(this.firestore, `beings/${this.beingId}/rawObservations`),
          orderBy('timestamp'),
        ),
      ),
      getDocs(collection(this.firestore, `beings/${this.beingId}/concepts`)),
      getDoc(
        doc(this.firestore, `beings/${this.beingId}/metadata/knowledgeState`),
      ),
    ]);

    const being: any = beingSnap.data() ?? {};
    this.beingName = being.name ?? this.beingId;
    this.alive = being.body?.alive !== false;
    this.deathTick = being.deathTick ?? null;
    this.genomeRows = Object.entries(being.genome ?? {})
      .filter(([, v]) => typeof v === 'number')
      .map(([k, v]) => ({ k, v: v as number }));

    this.obs = obsSnap.docs.map((d) => {
      const o: any = d.data();
      return {
        ts: o.timestamp?.toMillis?.() ?? 0,
        conceptId: `${o.terrain?.category || o.nominatim?.type || 'unknown'
          }:${o.nominatim?.type || 'unknown'}`,
        terrainCategory: o.terrain?.category ?? null,
        nominatimType: o.nominatim?.type ?? null,
        country: o.nominatim?.country ?? null,
        label: o.nominatim?.name || o.terrain?.category || 'unnamed',
        poiCount: Array.isArray(o.pois) ? o.pois.length : 0,
        isNovelVisit: o.comparison?.isNovelVisit === true,
        recognition: Number(o.comparison?.predictedAccuracy ?? 0),
      };
    });

    this.conceptRows = conceptSnap.docs
      .map((d) => {
        const c: any = d.data();
        return {
          id: c.id ?? d.id,
          occurrenceCount: c.occurrenceCount ?? 0,
          confidence: c.confidence ?? 0,
          avgPoiDensity: c.characteristics?.avgPoiDensity ?? 0,
          contradictions: c.contradictions ?? 0,
          firstEncountered: c.firstEncountered ?? 0,
          lastEncountered: c.lastEncountered ?? 0,
        };
      })
      .sort((a, b) => b.occurrenceCount - a.occurrenceCount);

    this.frames = this.replay(this.obs);
    this.series = this.buildSeries(this.frames);
    this.findings = this.analyse(
      this.obs,
      this.frames,
      being,
      ksSnap.data() as any,
    );
    this.chartAria = `Learning curves for ${this.beingName} across ${this.frames.length} observations.`;

    this.loading = false;
  }

  // ================================================================
  // REPLAY — mirrors updateKnowledgeState in concepts.ts
  // ================================================================

  private replay(obs: ObsPoint[]): ReplayFrame[] {
    const concepts = new Set<string>();
    const terrains = new Set<string>();
    let ema = 0.5;
    const novelFlags: number[] = [];
    const frames: ReplayFrame[] = [];

    obs.forEach((o, i) => {
      concepts.add(o.conceptId);
      if (o.terrainCategory) terrains.add(o.terrainCategory);
      if (o.nominatimType) terrains.add(o.nominatimType);

      ema = ema * 0.9 + o.recognition * 0.1;

      const cellsVisited = i + 1;
      const maturity = Math.min(
        (cellsVisited / 1000) *
        (terrains.size / 10) *
        ema *
        (o.isNovelVisit ? 1.2 : 1.0),
        1.0,
      );

      novelFlags.push(o.isNovelVisit ? 1 : 0);
      if (novelFlags.length > NOVEL_WINDOW) novelFlags.shift();
      const novelRate =
        novelFlags.reduce((a, b) => a + b, 0) / novelFlags.length;

      frames.push({
        i,
        conceptCount: concepts.size,
        recognitionEma: ema,
        maturity,
        novelRate,
      });
    });

    return frames;
  }

  // ================================================================
  // CHART
  // ================================================================

  private buildSeries(frames: ReplayFrame[]): Series[] {
    if (!frames.length) return [];

    const defs: Array<{
      key: keyof ReplayFrame;
      label: string;
      color: string;
      format: (v: number) => string;
    }> = [
        {
          key: 'conceptCount',
          label: 'concepts held',
          color: '#534AB7',
          format: (v) => String(Math.round(v)),
        },
        {
          key: 'recognitionEma',
          label: 'recognition',
          color: '#1D9E75',
          format: (v) => v.toFixed(2),
        },
        {
          key: 'novelRate',
          label: 'novel rate',
          color: '#D85A30',
          format: (v) => v.toFixed(2),
        },
        {
          key: 'maturity',
          label: 'maturity',
          color: '#888780',
          format: (v) => v.toFixed(4),
        },
      ];

    const innerW = CHART_W - PAD_L - PAD_R;
    const innerH = CHART_H - PAD_T - PAD_B;
    const n = frames.length;

    // Saturation: the last observation that introduced a concept.
    let satIndex = 0;
    for (let i = 1; i < n; i++) {
      if (frames[i].conceptCount > frames[i - 1].conceptCount) satIndex = i;
    }
    this.saturationX =
      n > 1 ? PAD_L + (satIndex / (n - 1)) * innerW : null;

    return defs.map((d) => {
      const vals = frames.map((f) => f[d.key] as number);
      const min = Math.min(...vals);
      const max = Math.max(...vals);
      const range = max - min || 1;

      const path = vals
        .map((v, i) => {
          const x = PAD_L + (n > 1 ? (i / (n - 1)) * innerW : innerW / 2);
          const y = PAD_T + innerH - ((v - min) / range) * innerH;
          return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
        })
        .join(' ');

      return {
        key: d.key as string,
        label: d.label,
        color: d.color,
        path,
        min,
        max,
        last: vals[vals.length - 1],
        format: d.format,
      };
    });
  }

  // ================================================================
  // ANALYSIS
  // ================================================================

  private analyse(
    obs: ObsPoint[],
    frames: ReplayFrame[],
    being: any,
    ks: any,
  ): Array<{ title: string; body: string; flag?: boolean }> {
    const out: Array<{ title: string; body: string; flag?: boolean }> = [];
    const n = frames.length;
    const last = frames[n - 1];

    let satIndex = 0;
    for (let i = 1; i < n; i++) {
      if (frames[i].conceptCount > frames[i - 1].conceptCount) satIndex = i;
    }
    const sinceSat = n - 1 - satIndex;
    const pctAfter = n > 1 ? Math.round((sinceSat / (n - 1)) * 100) : 0;

    out.push({
      title: 'Stopped forming concepts',
      body:
        sinceSat === 0
          ? `Still forming concepts — the most recent observation added one. ${last.conceptCount} held.`
          : `Last new concept at observation ${satIndex + 1} of ${n}. ` +
          `${sinceSat} observations (${pctAfter}% of its life) since, all recognised as things it already held.`,
      flag: pctAfter >= 60,
    });

    const singles = this.conceptRows.filter((c) => c.occurrenceCount === 1);
    out.push({
      title: 'Held once, never again',
      body: singles.length
        ? `${singles.length} of ${this.conceptRows.length} concepts were seen exactly once: ` +
        `${singles.slice(0, 4).map((c) => c.id).join(', ')}` +
        `${singles.length > 4 ? '…' : ''}. These carry a sample size of one and predict nothing.`
        : 'Every concept has been encountered more than once.',
      flag: singles.length > this.conceptRows.length / 2,
    });

    const investigated = (being.investigatedFeatures ?? []).length;
    const distinctFeatures = new Set(obs.map((o) => o.label)).size;
    out.push({
      title: 'Landmarks studied',
      body:
        `${investigated} feature${investigated === 1 ? '' : 's'} orbited to completion, ` +
        `across ${distinctFeatures} distinct places passed. ` +
        `Curiosity ${(being.genome?.curiosity ?? 0).toFixed(2)} sets how finely each was walked.`,
    });

    const recogRise = last.recognitionEma - 0.5;
    out.push({
      title: 'Recognition, not prediction',
      body:
        `Ends at ${last.recognitionEma.toFixed(2)}, ${recogRise >= 0 ? 'up' : 'down'} ` +
        `${Math.abs(recogRise).toFixed(2)} from the 0.50 seed. This is max similarity measured ` +
        `after arriving — it says nothing was predicted, only that something matched.`,
      flag: true,
    });

    const persisted = ks?.maturity;
    out.push({
      title: 'Maturity',
      body:
        `Replayed ${last.maturity.toFixed(4)}` +
        (typeof persisted === 'number'
          ? `, stored ${persisted.toFixed(4)}`
          : ', nothing stored yet') +
        `. Driven by cells visited and terrain variety, so it rewards travel rather than understanding.`,
    });

    const countries = new Set(obs.map((o) => o.country).filter(Boolean));
    const avgPois =
      obs.reduce((a, o) => a + o.poiCount, 0) / Math.max(obs.length, 1);
    out.push({
      title: 'Ground covered',
      body:
        `${countries.size} countr${countries.size === 1 ? 'y' : 'ies'}, ` +
        `${new Set(obs.map((o) => o.terrainCategory).filter(Boolean)).size} terrain categories, ` +
        `${avgPois.toFixed(1)} POIs per observation on average.`,
    });

    return out;
  }
}

